import {
  Button,
  Drawer,
  Flex,
  Grid,
  Layout as AntLayout,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { PylonChatProvider } from '@/components/helper/PylonChat';
import AtsReauthBanner from '@/components/integrations/AtsReauthBanner';
import useHirePortContext from '@/hooks/useHirePortContext';

import Navigation from './Navigation';
import { NavSiderProvider, useNavSider } from './NavSiderContext';

import type { ReactNode } from 'react';

interface LayoutProps {
  children: ReactNode;
}
const SIDER_WIDTH = 64;
const MOBILE_NOTIFIED_KEY = 'mobile-notified';

const LayoutShell = ({ children }: LayoutProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const navSider = useNavSider();
  const { colorScheme } = useHirePortContext();

  const isMobile = !screens.md;
  const collapsed = navSider?.collapsed ?? false;

  const [isNotified, setIsNotified] = useState(
    () => localStorage.getItem(MOBILE_NOTIFIED_KEY) != null,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleDismissNotify = () => {
    setIsNotified(true);
    localStorage.setItem(MOBILE_NOTIFIED_KEY, '1');
  };

  return (
    <>
      <PylonChatProvider />
      <AntLayout
        hasSider
        style={{
          backgroundColor: 'var(--ant-color-bg-container)',
        }}
      >
        <Typography.Link
          href="#main-content"
          style={{
            position: 'absolute',
            left: -9999,
            top: 8,
            zIndex: 1000,
            padding: '4px 8px',
            background: 'var(--ant-color-bg-container)',
          }}
          onFocus={(e) => {
            e.currentTarget.style.left = '8px';
          }}
          onBlur={(e) => {
            e.currentTarget.style.left = '-9999px';
          }}
        >
          {t('SKIP_TO_MAIN_CONTENT')}
        </Typography.Link>
        <AntLayout.Sider
          theme={colorScheme}
          width={SIDER_WIDTH}
          breakpoint="md"
          collapsible
          collapsed={collapsed}
          collapsedWidth={0}
          trigger={null}
          onCollapse={(nextCollapsed) => {
            navSider?.setCollapsed(nextCollapsed);
          }}
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'auto',
            borderInlineEnd: '1px solid var(--ant-color-split)',
          }}
        >
          <Navigation />
        </AntLayout.Sider>
        <AntLayout style={{ minWidth: 0, minHeight: 0 }}>
          <AntLayout.Content
            id="main-content"
            tabIndex={-1}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <AtsReauthBanner />
            <Flex
              vertical
              flex={1}
              style={{ minHeight: 0, overflowX: 'hidden', overflowY: 'auto' }}
            >
              {children}
            </Flex>
          </AntLayout.Content>
          {isMobile && (
            <Drawer
              size={'auto'}
              closable
              open={!isNotified}
              placement="top"
              getContainer={false}
              onClose={handleDismissNotify}
            >
              <Typography.Text>{t('NON_OPTIMIZED_TEXT')}</Typography.Text>
              <Button type="primary" onClick={handleDismissNotify}>
                {t('OK_TEXT')}
              </Button>
            </Drawer>
          )}
        </AntLayout>
      </AntLayout>
    </>
  );
};

const Layout = ({ children }: LayoutProps) => (
  <NavSiderProvider>
    <LayoutShell>{children}</LayoutShell>
  </NavSiderProvider>
);

export default Layout;
